/**
 * Almari · Pay CTA.
 *
 * Standalone pricing tiles + Razorpay checkout entry.
 * Structure follows hisaab pay-cta.ts; restyled with Almari's
 * warm-cream + brass + deep-ink design tokens (capsule atelier aesthetic).
 *
 * Tiers from PRICING.md (Issue II · MMXXVI):
 *   Pro     ₹999/yr   — CLIP vault, outfit recommender, wardrobe carbon, wishlist, family sharing
 *   Bundle  ₹2,999/yr — all 8 empire apps · single user
 *
 * notes: { brand: 'almari', tier } — multi-brand attribution on shared Razorpay account.
 */
import { checkout, TIER, priceLabel } from '../chassis/payment.ts';

type PayTier = 'pro' | 'family';

const TIERS: { id: PayTier; label: string; recommended?: true; desc: string }[] = [
  {
    id: 'pro',
    label: 'Almari Pro',
    recommended: true,
    desc: 'CLIP vault · outfit recommender · wardrobe carbon footprint · wishlist · 1 device.',
  },
  {
    id: 'family',
    label: 'Empire Bundle',
    desc: 'All 8 empire apps · single user · priority WhatsApp support.',
  },
];

const TPL = /* html */ `
<style>
@property --shimmer-pos {
  syntax: '<percentage>';
  inherits: false;
  initial-value: -40%;
}
:host {
  display: block;
  margin: 48px 0 28px;
  position: relative;
}
.shell {
  background:
    radial-gradient(circle at 14% 20%, color-mix(in oklch, oklch(72% 0.13 80) 12%, transparent) 0%, transparent 52%),
    radial-gradient(circle at 88% 80%, color-mix(in oklch, oklch(72% 0.13 80) 8%, transparent) 0%, transparent 40%),
    linear-gradient(180deg, oklch(96% 0.035 78) 0%, oklch(94% 0.04 75) 100%);
  border: 1px solid oklch(54% 0.13 75);
  padding: 32px 24px 24px;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
}
/* double-rule inset */
.shell::before {
  content: '';
  position: absolute;
  inset: 8px;
  border: 1px solid color-mix(in oklch, oklch(72% 0.13 80) 32%, transparent);
  border-radius: 2px;
  pointer-events: none;
}
/* shimmer sweep on hover */
.shell::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 35%, color-mix(in oklch, oklch(72% 0.13 80) 18%, transparent) 50%, transparent 65%);
  background-size: 200% 100%;
  background-position-x: var(--shimmer-pos);
  pointer-events: none;
  transition: --shimmer-pos 600ms cubic-bezier(0.16, 1, 0.3, 1);
}
.shell:hover::after { --shimmer-pos: 140%; }
.head { text-align: center; margin-bottom: 26px; }
.eyebrow {
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.46em;
  text-transform: uppercase;
  color: oklch(63% 0.12 78);
}
/* decorative rule under eyebrow */
.rule {
  width: 40px;
  height: 1px;
  background: linear-gradient(90deg, transparent, oklch(72% 0.13 80), transparent);
  margin: 8px auto 6px;
}
h3 {
  font-family: var(--display, serif);
  font-size: 28px;
  font-style: italic;
  font-weight: 600;
  margin: 0 0 6px;
  color: oklch(28% 0.14 25);
  letter-spacing: -0.01em;
}
.tagline {
  font-family: var(--display, serif);
  font-size: 15px;
  color: oklch(48% 0.10 25 / 0.8);
  font-style: italic;
  max-width: 38ch;
  margin: 0 auto;
  text-wrap: pretty;
}
.tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  margin-top: 14px;
}
.tier {
  background: oklch(97% 0.025 78);
  border: 1px solid oklch(54% 0.13 75);
  padding: 20px 16px 18px;
  border-radius: 2px;
  display: grid;
  gap: 9px;
  cursor: pointer;
  transition: transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 140ms ease;
  position: relative;
}
.tier:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px color-mix(in oklch, oklch(54% 0.13 75) 22%, transparent);
}
.tier.recommended {
  background: oklch(95% 0.04 78);
  border-color: oklch(72% 0.13 80);
  border-left: 3px solid oklch(54% 0.13 75);
}
.tier.recommended::after {
  content: 'recommended';
  position: absolute;
  top: -11px;
  left: 14px;
  background: oklch(54% 0.13 75);
  color: oklch(94% 0.04 75);
  font-family: var(--mono, monospace);
  font-size: 8px;
  padding: 3px 9px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
}
.label {
  font-family: var(--mono, monospace);
  font-size: 9.5px;
  letter-spacing: 0.38em;
  text-transform: uppercase;
  color: oklch(63% 0.12 78);
}
.price {
  font-family: var(--display, serif);
  font-size: 32px;
  font-style: italic;
  color: oklch(28% 0.14 25);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
}
.per {
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: oklch(58% 0.08 25 / 0.55);
}
.desc {
  font-family: var(--display, serif);
  font-size: 13.5px;
  color: oklch(38% 0.13 25);
  line-height: 1.5;
  font-style: italic;
}
button {
  appearance: none;
  font-family: var(--mono, monospace);
  font-size: 9.5px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  padding: 11px 10px;
  background: oklch(28% 0.14 25);
  color: oklch(94% 0.04 75);
  border: none;
  border-radius: 2px;
  cursor: pointer;
  margin-top: 8px;
  transition: filter 160ms ease, transform 120ms ease;
}
button:hover { filter: brightness(1.2); transform: translateY(-1px); }
button:active { transform: translateY(0); }
.tier.recommended button { background: oklch(54% 0.13 75); }
.footer {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dashed color-mix(in oklch, oklch(72% 0.13 80) 38%, transparent);
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-align: center;
  color: oklch(58% 0.08 25 / 0.5);
}
.success {
  margin-top: 18px;
  padding: 14px 16px;
  background: color-mix(in oklch, oklch(72% 0.13 80) 15%, transparent);
  border-left: 3px solid oklch(54% 0.13 75);
  font-family: var(--display, serif);
  font-style: italic;
  font-size: 15px;
  color: oklch(28% 0.14 25);
  display: none;
}
.success.show { display: block; }
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Closet Atelier &middot; MMXXVI</div>
    <div class="rule" aria-hidden="true"></div>
    <h3>Almari Pro: wardrobe intelligence.</h3>
    <div class="tagline">CLIP vault, outfit recommender, carbon footprint. Jo dikhta hai uska hisaab, jo nahi woh bhi.</div>
  </div>
  <div class="tiers" id="tier-grid"></div>
  <div class="success" id="success"></div>
  <div class="footer">Razorpay &middot; 30-day refund &middot; GST e-invoice &middot; DPDP-tight</div>
</section>
`;

export class AlmariPayCta extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const grid = this.#shadow.getElementById('tier-grid') as HTMLDivElement;
    const success = this.#shadow.getElementById('success') as HTMLDivElement;

    TIERS.forEach((t) => {
      const paise = TIER[t.id]?.amount_paise ?? 0;
      const priceStr = t.id === 'pro' ? '₹999' : '₹2,999';
      const card = document.createElement('div');
      card.className = `tier${t.recommended ? ' recommended' : ''}`;
      card.innerHTML = `
        <div class="label">${t.label}</div>
        <div class="price">${priceStr}</div>
        <div class="per">per year</div>
        <div class="desc">${t.desc}</div>
        <button data-tier="${t.id}">Buy ${t.label.split(' ')[0]}</button>
      `;
      grid.appendChild(card);
      void paise; // typecheck ref
    });

    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-tier]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset.tier as PayTier;
        if (!tier) return;
        try {
          const result = await checkout(tier, {});
          if (!result.dismissed && result.paymentId) {
            success.textContent = `Payment captured · id ${result.paymentId}. Activation email going to wickedrita@gmail.com.`;
            success.classList.add('show');
            localStorage.setItem(`almari:tier`, tier);
            localStorage.setItem(`almari:payment_id`, result.paymentId);
          }
        } catch (e) {
          success.textContent = `Checkout failed · ${e instanceof Error ? e.message : 'unknown'}. Email aditya@taxwalaai.com.`;
          success.classList.add('show');
        }
      });
    });

    void priceLabel; // typecheck ref
  }
}

customElements.define('almari-pay-cta', AlmariPayCta);
